import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { ThemeProvider, CssBaseline } from '@mui/material';
import buildTheme from './index';

const STORAGE_KEY = 'ats_theme_prefs';

function loadPrefs() {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    return {
      mode: raw.mode === 'dark' || raw.mode === 'light' ? raw.mode : null,
      compact: !!raw.compact,
    };
  } catch {
    return { mode: null, compact: false };
  }
}

const ThemeModeContext = createContext({
  mode: 'light',
  compact: false,
  toggleMode: () => {},
  setCompact: () => {},
});

export const useThemeMode = () => useContext(ThemeModeContext);

// RC18.2 Phase 10 — light/dark/compact preference, persisted, defaulting to the OS preference
// on first visit rather than always forcing light mode.
export const ThemeModeProvider = ({ children }) => {
  const [prefs, setPrefs] = useState(() => {
    const stored = loadPrefs();
    const systemPrefersDark = window.matchMedia?.('(prefers-color-scheme: dark)').matches;
    return {
      mode: stored.mode || (systemPrefersDark ? 'dark' : 'light'),
      compact: stored.compact,
    };
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  }, [prefs]);

  const toggleMode = useCallback(() => {
    setPrefs((p) => ({ ...p, mode: p.mode === 'dark' ? 'light' : 'dark' }));
  }, []);

  const setCompact = useCallback((compact) => {
    setPrefs((p) => ({ ...p, compact }));
  }, []);

  const theme = useMemo(() => buildTheme(prefs.mode, prefs.compact), [prefs.mode, prefs.compact]);

  const value = useMemo(
    () => ({ mode: prefs.mode, compact: prefs.compact, toggleMode, setCompact }),
    [prefs.mode, prefs.compact, toggleMode, setCompact]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export default ThemeModeProvider;
